// The explainer and per-platform videos the site plays: web encodes of the
// YouTube/TikTok cuts, made and uploaded by or-video's engine/pipeline
// site_video.sh and site_video_upload.sh to openreceive-public/videos/site/
// <version>/<video>/<lang>/{portrait,landscape}.mp4, served through CloudFront
// (never the S3 origin). config/site_videos.json lists what exists; it is
// written by tools/sync-site-videos.py, along with the landscape captions under
// public/captions/<version>/, which the site serves itself because CloudFront
// sends no CORS headers.
import siteVideosJson from "../../../../../config/site_videos.json" with { type: "json" }
import siteVideoPagesJson from "../../../../../config/site_video_pages.json" with { type: "json" }

const SITE_VIDEOS = siteVideosJson.videos

export type SiteVideoId = keyof typeof SITE_VIDEOS
export type SiteVideoOrientation = "portrait" | "landscape"

// config/site_video_pages.json names a video per page by hand; one that is not
// in the manifest (not encoded or uploaded yet) falls back to the explainer.
const known = (video: string | undefined): SiteVideoId | null =>
  video && video in SITE_VIDEOS ? (video as SiteVideoId) : null

const FALLBACK: SiteVideoId = known(siteVideoPagesJson.fallback) ?? "explainer"

export const homepageVideo = (): SiteVideoId => known(siteVideoPagesJson.homepage) ?? FALLBACK

// The techtalk at the top of /guides. No fallback: the index shows no video
// until its own is synced.
export const guidesVideo = (): SiteVideoId | null => known(siteVideoPagesJson.guides)

export const frameworkVideo = (id: string, viaBtcpay: boolean): SiteVideoId =>
  known((siteVideoPagesJson.frameworks as Record<string, string>)[id]) ??
  (viaBtcpay ? known(siteVideoPagesJson.viaBtcpay) : null) ??
  FALLBACK

export const platformVideo = (id: string): SiteVideoId =>
  known((siteVideoPagesJson.platforms as Record<string, string>)[id]) ?? FALLBACK

// A browser tag the videos don't have under its own name.
const LANG_ALIASES: Record<string, string> = {
  es: "es-419",
  pt: "pt-BR",
  hi: "hi-Latn",
  tl: "fil",
  zh: "zh-CN",
  "zh-hans": "zh-CN",
  "zh-sg": "zh-CN",
  "zh-hant": "zh-TW",
  // Hong Kong and Macau speak Cantonese: the yue cut, not the Mandarin zh-TW one.
  "zh-hk": "yue",
  "zh-mo": "yue"
}

// The browser's first preferred language this video has, else English. An
// English preference ahead of another language wins, so en-first browsers
// always get the English cut.
export const siteVideoLang = (
  video: SiteVideoId,
  preferred: readonly string[] = navigator.languages ?? [navigator.language]
): string => {
  const available: readonly string[] = SITE_VIDEOS[video].langs
  const byLower = new Map(available.map((lang) => [lang.toLowerCase(), lang]))
  for (const tag of preferred) {
    const lower = tag.toLowerCase()
    // zh-Hant-HK: the region decides first, then the script.
    const parts = lower.split("-")
    const candidates = [
      lower,
      LANG_ALIASES[lower],
      parts.length > 2 ? LANG_ALIASES[`${parts[0]}-${parts[2]}`] : undefined,
      parts.length > 1 ? LANG_ALIASES[`${parts[0]}-${parts[1]}`] : undefined,
      parts[0],
      LANG_ALIASES[parts[0]]
    ]
    for (const candidate of candidates) {
      const match = candidate && byLower.get(candidate.toLowerCase())
      if (match) return match
    }
  }
  return "en"
}

// The techtalk is landscape-only: phones play the landscape cut too.
export const siteVideoHasPortrait = (video: SiteVideoId): boolean => SITE_VIDEOS[video].portrait

export type SiteVideoChapter = { start: number; title: string }

// From the English chapters.txt, so only for the English cut; empty for most videos.
export const siteVideoChapters = (video: SiteVideoId, lang: string): readonly SiteVideoChapter[] =>
  lang === "en" ? ((SITE_VIDEOS[video].en as { chapters?: SiteVideoChapter[] }).chapters ?? []) : []

export const siteVideoUrl = (video: SiteVideoId, lang: string, orientation: SiteVideoOrientation): string =>
  `${siteVideosJson.baseUrl}/${video}/${lang}/${orientation}.mp4`

// A frame of the agent at work (or plugin setup), never the YouTube/X thumbnail.
export const siteVideoPosterUrl = (video: SiteVideoId, lang: string, orientation: SiteVideoOrientation): string =>
  `${siteVideosJson.baseUrl}/${video}/${lang}/poster-${orientation}.webp`

// Same-origin WebVTT for a landscape cut, or null: the portrait cuts have their
// captions burned in, and most landscape cuts have no caption file.
export const siteVideoCaptionsUrl = (
  video: SiteVideoId,
  lang: string,
  orientation: SiteVideoOrientation
): string | null => {
  const captions: readonly string[] = SITE_VIDEOS[video].captions
  return orientation === "landscape" && captions.includes(lang)
    ? `${siteVideosJson.captionsPath}/${video}/${lang}/landscape.vtt`
    : null
}
